import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Link,
  TextField,
} from '@mui/material';
import {
  Assignment,
  CheckCircle,
  Cancel,
  Refresh,
  OpenInNew,
  Warning,
} from '@mui/icons-material';
import { ProcessActivityApi, UserTask } from '../types/process';
import { getCurrentEnvironment } from '../config/environment';

interface UserTasksProps {
  /** Lista de atividades da instância */
  activities: ProcessActivityApi[];
  /** ID da instância do processo */
  instanceId: string;
  /** ID da definição do processo */
  processDefinitionId: string;
  /** Nome do tenant */
  tenantName: string;
  /** Callback para recriar tarefa */
  onRecreateTask: (activityId: string) => Promise<void>;
}

export const UserTasks: React.FC<UserTasksProps> = ({
  activities,
  onRecreateTask,
}) => {
  const [recreatingTaskId, setRecreatingTaskId] = useState<string | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<UserTask | null>(null);
  const [confirmText, setConfirmText] = useState<string>('');

  // Obter URL base do Cockpit baseado no ambiente
  const environment = getCurrentEnvironment();
  const cockpitBaseUrl = environment === 'production'
    ? 'https://cockpit.bhub.ai'
    : 'https://cockpit.cexp-dev.bhub.ai';

  // Filtrar apenas as user tasks
  const userTasks = useMemo<UserTask[]>(() => {
    return activities
      .filter(activity => activity.activity_type === 'userTask')
      .map(activity => ({
        id: activity.id,
        activity_id: activity.activity_id,
        activity_name: activity.activity_name,
        task_id: activity.task_id,
        start_time: activity.start_time,
        end_time: activity.end_time,
        duration_in_millis: activity.duration_in_millis,
        canceled: activity.canceled,
        // Tarefas podem ser recriadas apenas se NÃO estão canceladas E estão em aberto
        is_open: activity.canceled !== true && (activity.end_time === null || activity.duration_in_millis === null),
      }));
  }, [activities]);

  // Função para formatar data
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Função para formatar duração
  const formatDuration = (millis: number | null): string => {
    if (millis === null) return '-';
    
    const seconds = Math.floor(millis / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // Função para obter o status da tarefa
  const getTaskStatus = (task: UserTask) => {
    if (task.canceled) return { label: 'Cancelada', color: 'error' as const };
    if (task.end_time && task.duration_in_millis !== null) return { label: 'Concluída', color: 'success' as const };
    return { label: 'Em Aberto', color: 'warning' as const };
  };

  // Função para abrir diálogo de confirmação
  const handleOpenConfirmDialog = (task: UserTask) => {
    setSelectedTask(task);
    setConfirmDialogOpen(true);
  };

  // Função para fechar diálogo de confirmação
  const handleCloseConfirmDialog = () => {
    setConfirmDialogOpen(false);
    setSelectedTask(null);
    setConfirmText('');
  };

  // Função para recriar tarefa
  const handleRecreateTask = async () => {
    if (!selectedTask) return;

    setRecreatingTaskId(selectedTask.activity_id);
    setConfirmDialogOpen(false);

    try {
      await onRecreateTask(selectedTask.activity_id);
    } finally {
      setRecreatingTaskId(null);
      setSelectedTask(null);
    }
  };

  // Função para obter link do Cockpit
  const getCockpitLink = (taskId: string | null): string | null => {
    if (!taskId) return null;
    return `${cockpitBaseUrl}/task-manager/task/${taskId}`;
  };

  if (userTasks.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info" icon={<Assignment />}>
          Nenhuma tarefa de usuário encontrada nesta instância de processo.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment />
          Tarefas de Usuário ({userTasks.length})
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="body2">
          Esta seção exibe todas as tarefas de usuário (userTask) desta instância de processo. 
          Você pode recriar tarefas que estejam <strong>em aberto</strong> (não concluídas e não canceladas) para reiniciá-las.
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Observação:</strong> Tarefas canceladas não podem ser recriadas.
        </Typography>
      </Alert>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Nome da Tarefa</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Início</strong></TableCell>
              <TableCell><strong>Fim</strong></TableCell>
              <TableCell><strong>Duração</strong></TableCell>
              <TableCell align="center"><strong>Ações</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userTasks.map((task) => {
              const status = getTaskStatus(task);
              const cockpitLink = getCockpitLink(task.task_id);
              const canRecreate = task.is_open;

              return (
                <TableRow key={task.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Assignment fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.activity_name || task.activity_id}
                      </Typography>
                      {task.task_id && (
                        <Tooltip title="ID da Task">
                          <Chip
                            label={task.task_id.substring(0, 8)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={status.label}
                      color={status.color}
                      size="small"
                      icon={
                        status.label === 'Concluída' ? <CheckCircle /> :
                        status.label === 'Cancelada' ? <Cancel /> :
                        <Warning />
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {formatDate(task.start_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {formatDate(task.end_time)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                      {formatDuration(task.duration_in_millis)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      {cockpitLink && (
                        <Tooltip title="Abrir no Cockpit">
                          <IconButton
                            size="small"
                            color="primary"
                            component={Link}
                            href={cockpitLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <OpenInNew fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {canRecreate && (
                        <Tooltip title="Recriar Tarefa">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleOpenConfirmDialog(task)}
                              disabled={recreatingTaskId === task.activity_id}
                            >
                              {recreatingTaskId === task.activity_id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <Refresh fontSize="small" />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo de confirmação para recriar tarefa */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning color="error" />
            <Typography variant="h6" component="span">
              ⚠️ Ação Crítica - Confirmar Recriação de Tarefa
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
            Você está prestes a recriar a tarefa: <strong>{selectedTask?.activity_name || selectedTask?.activity_id}</strong>
          </Typography>
          
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              Esta é uma operação crítica que irá:
            </Typography>
            <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
              <li>Cancelar a atividade atual no processo</li>
              <li>Criar uma nova instância da atividade</li>
              <li><strong>Gerar uma nova tarefa no GTO (Gestor de Tarefas do Operador)</strong></li>
            </ul>
          </Alert>

          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              ⚠️ ATENÇÃO IMPORTANTE:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Caso uma tarefa tenha sido criada anteriormente no GTO, ela <strong>NÃO será deletada automaticamente</strong>.
            </Typography>
            <Typography variant="body2">
              Você precisará <strong>deletar manualmente a tarefa antiga</strong> no sistema GTO para evitar duplicação.
            </Typography>
          </Alert>

          <Box sx={{ 
            p: 2, 
            bgcolor: 'grey.100', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Para confirmar esta ação, digite <strong>confirm</strong> no campo abaixo:
            </Typography>
            <TextField
              fullWidth
              size="small"
              value={confirmText}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirmText(e.target.value)}
              placeholder="Digite 'confirm' para continuar"
              autoFocus
              sx={{ 
                bgcolor: 'white',
                '& .MuiOutlinedInput-root': {
                  '&.Mui-focused fieldset': {
                    borderColor: confirmText === 'confirm' ? 'success.main' : 'primary.main',
                  },
                },
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={handleCloseConfirmDialog} 
            color="inherit"
            variant="outlined"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRecreateTask}
            variant="contained"
            color="error"
            startIcon={<Refresh />}
            disabled={confirmText !== 'confirm'}
          >
            Recriar Tarefa
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

